import cn from 'classnames'
import React, { ReactNode, useEffect, useState } from 'react'

import { useDevice } from '@/context/Device'
import { Spacings, theme } from '@/design-lib/theme'

import Icon from '../Icon'

import { controlContainer, itemsContainer, item, carousel, controlButton } from './styles.css'

type Props = {
  children: ReactNode[]
  className?: string
  hasInnerNextButton?: boolean
  itemsGap: Spacings
  itemsWidth: number
  lastChild?: ReactNode
  onNextClicked?: () => void
  onPrevClicked?: () => void
}

export const ItemsCarousel: React.FC<Props> = ({
  children,
  lastChild,
  itemsGap,
  itemsWidth,
  onNextClicked = () => {},
  onPrevClicked = () => {},
  className,
  hasInnerNextButton,
}) => {
  const device = useDevice()
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [childrenParsed, setChildrenParsed] = useState<ReactNode[]>(children)
  const [itemsContainerRef, setItemsContainerRef] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!itemsContainerRef) return
    itemsContainerRef.scrollTo({
      behavior: 'smooth',
      left: (itemsWidth + parseInt(theme.spacings[itemsGap])) * currentIndex,
    })
  }, [currentIndex])

  const shouldLeftArrowBeVisible = (): boolean => {
    return device === 'desktop' && currentIndex > 0
  }

  const shouldRightArrowBeVisible = (): boolean => {
    if (device !== 'desktop') return false

    const lastScrollableIndex = calcLastScrollableIndex()
    return lastScrollableIndex !== undefined && currentIndex < lastScrollableIndex
  }

  const calcLastScrollableIndex = (): number | undefined => {
    if (!itemsContainerRef) return undefined
    const carouselWidth = itemsContainerRef.clientWidth
    const totalVisibleItems = Math.floor(carouselWidth / itemsWidth)

    return childrenParsed.length - totalVisibleItems
  }

  const goToPrevious = () => {
    setCurrentIndex(currentIndex - 1)
    onPrevClicked()
  }

  const goToNext = () => {
    setCurrentIndex(currentIndex + 1)
    onNextClicked()
  }

  useEffect(() => {
    const addLastChild = (children: ReactNode[], lastChild: ReactNode) => {
      return [...children, lastChild]
    }

    if (lastChild) {
      setChildrenParsed(addLastChild(children, lastChild))
    } else {
      setChildrenParsed(children)
    }
  }, [lastChild, children])

  return (
    <div className={cn(carousel, className)}>
      <div ref={setItemsContainerRef} className={itemsContainer} style={{ gap: theme.spacings[itemsGap] }}>
        {childrenParsed.map((child, index) => (
          <div key={index} className={item} style={{ height: 'auto', width: itemsWidth }}>
            {child}
          </div>
        ))}
      </div>

      {shouldLeftArrowBeVisible() && (
        <div className={controlContainer.left}>
          <button aria-label="prev" className={controlButton.left} onClick={goToPrevious}>
            <Icon.ArrowUp style={{ transform: 'rotate(-90deg)' }} />
          </button>
        </div>
      )}

      {shouldRightArrowBeVisible() && (
        <div className={controlContainer.right}>
          <button
            aria-label="next"
            className={controlButton.right}
            style={{ right: hasInnerNextButton ? 24 : '' }}
            onClick={goToNext}
          >
            <Icon.ArrowUp style={{ transform: 'rotate(90deg)' }} />
          </button>
        </div>
      )}
    </div>
  )
}
